import { motion } from "framer-motion";
import { ScanFace, Sparkles, Scissors, ImagePlus, Ruler, Download, User } from "lucide-react";
import { MarquardtMask } from "@/components/MarquardtMask";

const BENEFITS = [
  {
    icon: ScanFace,
    title: "Маска и линии на вашем лице",
    text: "Маска Марквардта и линии золотого сечения ложатся на фото с точностью до миллиметра. Двигайте, растягивайте и поворачивайте пальцами — рассмотрите себя внимательно.",
  },
  {
    icon: Sparkles,
    title: "Честный ИИ-разбор",
    text: "Форма лица и баллы по зонам — по реальным замерам 68 точек лица, а не на глаз. Как консультация у стилиста, только за минуту и без записи.",
  },
  {
    icon: Scissors,
    title: "Примерка до стрижки",
    text: "Примеряйте разные варианты причёсок на собственном фото. Сравнивайте результаты и выбирайте образ, который хотите обсудить с мастером.",
  },
  {
    icon: ImagePlus,
    title: "Стрижка по референсу",
    text: "Нашли подходящую причёску на фото? Загрузите референс и посмотрите визуализацию выбранного образа на своём фото.",
  },
  {
    icon: Ruler,
    title: "Техкарта для мастера",
    text: "В тарифе «Про»: зоны, длины, углы оттяжки и порядок работы. Мастер повторит стрижку один в один, без «ну примерно так».",
  },
  {
    icon: Download,
    title: "Сохранение в HD",
    text: "Фото с маской и результаты примерок скачиваются в полном качестве — покажите мастеру или оставьте себе.",
  },
  {
    icon: User,
    title: "Личный кабинет",
    text: "История разборов и примерок, остаток генераций и тариф — всё в одном месте. Вход по коду из Telegram или с почты.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Benefits = () => (
  <section
    id="benefits"
    className="relative overflow-hidden px-6 py-28 md:px-12 md:py-40 lg:px-24"
    data-testid="benefits-section"
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/3 top-0 h-[600px] w-[600px] rounded-full bg-white/[0.03] blur-[120px]"
    />
    <div className="pointer-events-none absolute -left-48 bottom-0 hidden w-[560px] opacity-[0.05] lg:block">
      <MarquardtMask breathing={false} />
    </div>

    <div className="relative">
      <motion.p
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        custom={0}
        className="mb-8 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]"
      >
        ( Преимущества )
      </motion.p>
      <motion.h2
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        custom={1}
        className="mb-16 max-w-3xl font-cormorant text-4xl font-light leading-[1.05] tracking-tight text-[#F4F4F0] md:mb-24 md:text-6xl"
      >
        Что внутри
      </motion.h2>

      <div className="grid gap-x-20 md:grid-cols-2">
        {BENEFITS.map((benefit, i) => (
          <motion.div
            key={benefit.title}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            custom={i}
            className="group border-t border-white/10 py-10 transition-colors duration-500 hover:border-[#C2A878]/30 md:py-14"
            data-testid={`benefit-item-${i + 1}`}
          >
            <div className="flex items-start gap-6">
              <span className="font-jetbrains text-xs tracking-[0.2em] text-[#525252] transition-colors duration-500 group-hover:text-[#C2A878]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <benefit.icon
                size={22}
                strokeWidth={1.5}
                className="mt-0.5 shrink-0 text-[#525252] transition-colors duration-500 group-hover:text-[#C2A878]"
              />
              <div>
                <h3 className="font-cormorant text-2xl font-light text-[#F4F4F0] md:text-4xl">
                  {benefit.title}
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-[#A3A3A3] md:text-base">
                  {benefit.text}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="border-t border-white/10" />
    </div>
  </section>
);
